<template>
  <v-app class="agent-portal">
    <v-overlay :value="loader">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Agent Portal Header -->
    <HeaderAgent />

    <v-main class="auth-pages" style="background-color: #FAC500;">
      <!-- Hero Section -->
      <section class="hero-section mt-10">
        <v-container fluid class="hero-container">
          <div class="hero-content-wrapper">
            <!-- Commission Info at Top -->
            <div class="commission-header">
              <h2 class="commission-title">
                Earn $385 Per<br>
                Car—Plus <em>Residuals</em>
              </h2>
              <p class="commission-example">Become an AutoSyncX™ Referring Agent</p>
            </div>

            <!-- Car Image in Center -->
            <div class="car-display-center">
              <img src="@/assets/img/tesla-model-s.png" alt="Premium Tesla" class="hero-car-image" />
              <div class="referral-card-overlay">
                <div class="referral-card">
                  <div class="card-header">
                    <div class="agent-avatar">
                      <i class="material-icons-round">person</i>
                    </div>
                    <span class="agent-title">$385</span>
                    <span class="agent-subtitle">Referring Agent</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Below -->
            <div class="hero-main-content">
              <h1 class="hero-title">
                Share cars, connect buyers,<br>
                and earn commissions on every deal.
              </h1>
              <div class="hero-buttons">
                <v-btn
                  class="hero-cta-btn"
                  :large="!$vuetify.breakpoint.xs"
                  :small="$vuetify.breakpoint.xs"
                  dark
                  @click="startReferring"
                >
                  Start Referring Today
                </v-btn>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Features Section -->
      <section class="features-section">
        <v-container>
          <v-row justify="center">
            <v-col cols="12" md="4" class="text-center">
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="material-icons-round">attach_money</i>
                </div>
                <h3 class="feature-title">Big Earnings</h3>
                <p class="feature-description">
                  $385 per car sold<br>
                  through your referral link.
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-center">
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="material-icons-round">schedule</i>
                </div>
                <h3 class="feature-title">Residual Income</h3>
                <p class="feature-description">
                  Earn overrides via<br>
                  when you've cut derships.
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-center">
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="material-icons-round">dashboard</i>
                </div>
                <h3 class="feature-title">Instant Tools</h3>
                <p class="feature-description">
                  Your digital<br>
                  referral card • dashboard
                </p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- How It Works Section -->
      <section class="how-it-works-section">
        <v-container>
          <v-row>
            <v-col cols="12" md="6">
              <div class="how-it-works-content">
                <h2 class="section-title">How It Works</h2>
                <div class="steps-list">
                  <div class="step-item">
                    <div class="step-number">1</div>
                    <div class="step-content">
                      <h4>Sign up as an agent.</h4>
                      <p>Create your digital referral card.</p>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">2</div>
                    <div class="step-content">
                      <h4>Share cars.</h4>
                      <p>Send your personalized link.</p>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">3</div>
                    <div class="step-content">
                      <h4>Buyers close deals.</h4>
                      <p>Dealers pay commissions.</p>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="earnings-transparency">
                <h3 class="transparency-title">Earnings Transparency</h3>
                <div class="earnings-display">
                  <div class="earnings-row">
                    <span class="earnings-label">Enter Cars Sold</span>
                    <span class="earnings-value">10</span>
                  </div>
                  <div class="earnings-total">
                    <span class="total-label">Earned Total:</span>
                    <span class="total-amount">$3,850</span>
                  </div>
                  <div class="bonus-section">
                    <span class="bonus-label">Bonus! Add recruited dealerships → earn extra</span>
                    <span class="bonus-percentage">3%</span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Testimonials Section -->
      <section class="testimonials-section">
        <v-container>
          <h2 class="section-title text-center mb-8">What Our Agents Say</h2>
          <v-row>
            <v-col cols="12" md="6" v-for="testimonial in testimonials" :key="testimonial.id">
              <div class="testimonial-card">
                <div class="testimonial-header">
                  <v-avatar size="60" class="testimonial-avatar">
                    <img :src="testimonial.avatar" :alt="testimonial.name" />
                  </v-avatar>
                  <div class="testimonial-info">
                    <h4 class="testimonial-name">{{ testimonial.name }}</h4>
                    <div class="testimonial-rating">
                      <v-rating
                        :value="testimonial.rating"
                        color="amber"
                        dense
                        half-increments
                        readonly
                        size="16"
                      ></v-rating>
                    </div>
                  </div>
                </div>
                <p class="testimonial-text">"{{ testimonial.text }}"</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Professional CTA Section -->
      <section class="cta-section">
        <v-container>
          <div class="cta-content">
            <div class="cta-text">
              <h2 class="cta-title">Ready to Transform Your Income?</h2>
              <p class="cta-subtitle">Join thousands of agents who've increased their earnings by 40% with our platform</p>
              <div class="cta-features">
                <div class="cta-feature">
                  <i class="material-icons-round">verified</i>
                  <span>No Setup Fees</span>
                </div>
                <div class="cta-feature">
                  <i class="material-icons-round">schedule</i>
                  <span>5-Minute Setup</span>
                </div>
                <div class="cta-feature">
                  <i class="material-icons-round">support_agent</i>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
            <div class="cta-actions">
              <v-btn
                class="cta-primary-btn"
                large
                dark
                @click="startReferring"
              >
                Start Earning Today
              </v-btn>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Contact Section -->
      <section class="contact-section">
        <v-container>
          <div class="contact-content">
            <h2 class="contact-title">CONTACT</h2>
            <div class="contact-buttons">
              <v-btn
                class="contact-btn call-btn"
                :large="!$vuetify.breakpoint.xs"
                :small="$vuetify.breakpoint.xs"
                @click="contactAgent"
                :loading="callAgentLoading"
              >
                <i class="material-icons-round btn-icon">phone</i>
                Call Agent
              </v-btn>
              <v-btn
                class="contact-btn chat-btn"
                :large="!$vuetify.breakpoint.xs"
                :small="$vuetify.breakpoint.xs"
                @click="chatWithAgent"
                :loading="chatAgentLoading"
              >
                <i class="material-icons-round btn-icon">chat</i>
                Chat Agent
              </v-btn>
            </div>
          </div>
        </v-container>
      </section>
    </v-main>

    <!-- Agent Portal Footer -->
    <FooterAgent />

    <!-- Agent Contact Modal -->
    <AgentContactModal
      v-if="showAgentModal"
      :mode="agentMode"
      @close="closeAgentModal"
    />
  </v-app>
</template>

<script>
import HeaderAgent from '@/components/Layout/HeaderAgent.vue'
import FooterAgent from '@/components/Layout/FooterAgent.vue'
import AgentContactModal from '@/components/Agents/AgentContactModal.vue'

export default {
  name: 'ReferringAgentHome',
  components: {
    HeaderAgent,
    FooterAgent,
    AgentContactModal
  },
  data() {
    return {
      loader: false,
      showAgentModal: false,
      agentMode: 'chat',
      callAgentLoading: false,
      chatAgentLoading: false,
      hasVoiceAgent: false,
      voiceAgentData: null,
      testimonials: [
        {
          id: 1,
          name: 'David R.',
          avatar: require("@/assets/img/bruce-mars.jpg"),
          rating: 5,
          text: 'I earned $1,858 in first month as a Referring Agent.'
        },
        {
          id: 2,
          name: 'Agent**',
          avatar: require("@/assets/img/marie.jpg"),
          rating: 5,
          text: 'Sharing cars is easier than I imagined. AutoSyncX™ is my side hustle turned career.'
        }
      ]
    }
  },
  mounted() {
    // Load agent voice agent data
    this.loadAgentVoiceAgent();
  },
  methods: {
    startReferring() {
      // Open agent chat modal for signup
      this.agentMode = 'chat';
      this.showAgentModal = true;
      this.$toast.success('Opening chat with Alex, your AutoSyncX™ representative...', {
        position: 'top-right'
      });
    },

    // Load agent voice agent configuration
    async loadAgentVoiceAgent() {
      try {
        console.log('🎤 Loading agent voice agent configuration...');

        // Call API to check if agent voice agent is configured
        const response = await axios.get('/api/agent/voice-agent/check-availability');

        if (response.data.flag === 1 && response.data.data) {
          this.voiceAgentData = response.data.data;
          this.hasVoiceAgent = response.data.data.has_voice_agent && response.data.data.voice_agent_enabled;

          console.log('✅ Agent voice agent loaded:', {
            hasVoiceAgent: this.hasVoiceAgent,
            agentId: this.voiceAgentData.retell_agent_id
          });
        } else {
          console.log('ℹ️ No agent voice agent configured');
          this.hasVoiceAgent = false;
        }
      } catch (error) {
        console.error('❌ Error loading agent voice agent:', error);
        this.hasVoiceAgent = false;
      }
    },

    // Handle Call Agent button click
    async contactAgent() {
      this.callAgentLoading = true;

      try {
        // Open modal in voice mode
        this.agentMode = 'voice';
        this.showAgentModal = true;
        this.$toast.success('Opening conversation with Alex, your AutoSyncX™ representative...');

      } catch (error) {
        console.error('❌ Error opening agent modal:', error);
        this.$toast.error('Failed to open conversation. Please try again.');
      } finally {
        this.callAgentLoading = false;
      }
    },

    // Handle Chat Agent button click
    async chatWithAgent() {
      this.chatAgentLoading = true;

      try {
        // Open modal in chat mode
        this.agentMode = 'chat';
        this.showAgentModal = true;
        this.$toast.success('Opening chat with Alex, your AutoSyncX™ representative...');

      } catch (error) {
        console.error('❌ Error opening chat modal:', error);
        this.$toast.error('Failed to open chat. Please try again.');
      } finally {
        this.chatAgentLoading = false;
      }
    },

    closeAgentModal() {
      this.showAgentModal = false;
    }
  }
}
</script>

<style scoped>
/* Global Styles */
* {
  box-sizing: border-box;
}

.agent-portal {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #f8f9fa;
  padding: 0;
  margin: 0;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: auto;
  display: flex;
  align-items: center;
  color: #2c3e50;
  padding: 60px 0;
  position: relative;
}

.hero-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.hero-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 40px;
}

.commission-header {
  text-align: center;
}

.commission-title {
  font-size: 3.2rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1a202c;
  margin-bottom: 8px;
}

.commission-title em {
  color: #007bff;
  font-style: normal;
}

.commission-example {
  font-size: 1.2rem;
  color: #6c757d;
  font-weight: 500;
}

.car-display-center {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-car-image {
  max-width: 500px;
  width: 100%;
  height: auto;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
}

.referral-card-overlay {
  position: absolute;
  top: -20px;
  right: -50px;
  z-index: 5;
}

.referral-card {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  border-radius: 16px;
  padding: 20px;
  color: white;
  box-shadow: 0 15px 35px rgba(74, 144, 226, 0.3);
  min-width: 180px;
  text-align: center;
}

.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.agent-avatar {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-title {
  font-size: 1.8rem;
  font-weight: 700;
}

.agent-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
}

.hero-main-content {
  text-align: center;
  max-width: 800px;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1a202c;
  margin-bottom: 32px;
}

.hero-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
}

.hero-cta-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  color: white !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3) !important;
  transition: all 0.3s ease !important;
}

.hero-cta-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 12px 35px rgba(0, 123, 255, 0.4) !important;
}

/* Features Section */
.features-section {
  background: white;
  padding: 80px 0;
}

.feature-card {
  padding: 40px 30px;
  text-align: center;
  height: 100%;
}

.feature-icon {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 2rem;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 16px;
}

.feature-description {
  font-size: 1rem;
  color: #6c757d;
  line-height: 1.6;
}

/* How It Works Section */
.how-it-works-section {
  background: #f8f9fa;
  padding: 80px 0;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 40px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.step-number {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.step-content h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
}

.step-content p {
  font-size: 1rem;
  color: #6c757d;
  line-height: 1.6;
}

.earnings-transparency {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.transparency-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 24px;
}

.earnings-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.earnings-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.earnings-label {
  font-size: 1rem;
  color: #6c757d;
}

.earnings-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a202c;
}

.earnings-total {
  background: linear-gradient(135deg, #20c997 0%, #17a085 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 1.1rem;
  font-weight: 600;
}

.total-amount {
  font-size: 1.8rem;
  font-weight: 800;
}

.bonus-section {
  background: linear-gradient(135deg, #20c997 0%, #17a085 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bonus-label {
  font-size: 0.9rem;
  font-weight: 500;
}

.bonus-percentage {
  font-size: 1.5rem;
  font-weight: 800;
}

/* Testimonials Section */
.testimonials-section {
  background: white;
  padding: 80px 0;
}

.testimonial-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 30px;
  height: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.testimonial-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.testimonial-avatar {
  border: 3px solid #007bff;
}

.testimonial-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.testimonial-rating {
  margin-top: 4px;
}

.testimonial-text {
  font-size: 1rem;
  color: #495057;
  line-height: 1.6;
  font-style: italic;
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  padding: 80px 0;
  color: white;
}

.cta-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
}

.cta-text {
  flex: 1;
}

.cta-title {
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 16px;
}

.cta-subtitle {
  font-size: 1.2rem;
  color: #cbd5e0;
  margin-bottom: 32px;
  line-height: 1.6;
}

.cta-features {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.cta-feature {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #20c997;
}

.cta-feature i {
  font-size: 1.5rem;
}

.cta-feature span {
  font-size: 1rem;
  font-weight: 600;
}

.cta-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.cta-primary-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  color: white !important;
  padding: 18px 36px !important;
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3) !important;
  transition: all 0.3s ease !important;
  min-width: 250px !important;
}

.cta-primary-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 12px 35px rgba(0, 123, 255, 0.4) !important;
}

/* Contact Section */
.contact-section {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  padding: 80px 0;
  color: white;
  text-align: center;
}

.contact-content {
  max-width: 600px;
  margin: 0 auto;
}

.contact-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 40px;
  color: white;
  font-family: 'Inter', sans-serif;
}

.contact-buttons {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 60px;
  align-items: center;
}

.contact-btn {
  width: 280px;
  height: 60px;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.call-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
}

.call-btn:hover {
  background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
}

.chat-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border: none;
}

.chat-btn:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.4);
}

.btn-icon {
  font-size: 1.3rem;
}

/* Responsive Design */
@media (max-width: 960px) {
  .hero-section {
    min-height: auto;
    padding: 80px 0;
  }

  .hero-title {
    font-size: 2.8rem;
  }

  .commission-title {
    font-size: 2.5rem;
  }

  .referral-card-overlay {
    position: static;
    margin-top: 20px;
  }

  .hero-car-image {
    max-width: 350px;
  }

  .cta-content {
    flex-direction: column;
    text-align: center;
    gap: 40px;
  }

  .cta-features {
    justify-content: center;
  }

  .contact-section {
    padding: 60px 0;
  }

  .contact-title {
    font-size: 2.5rem;
    margin-bottom: 30px;
  }

  .contact-btn {
    width: 250px;
    height: 55px;
    font-size: 1rem;
  }

  .contact-buttons {
    margin-bottom: 40px;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 2rem;
  }

  .commission-title {
    font-size: 2rem;
  }

  .hero-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .hero-cta-btn {
    width: 100%;
    max-width: 280px;
  }

  .hero-car-image {
    max-width: 250px;
  }

  .cta-title {
    font-size: 2rem;
  }

  .cta-subtitle {
    font-size: 1.1rem;
  }

  .cta-features {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .section-title {
    font-size: 2rem;
  }

  .feature-card {
    padding: 30px 20px;
  }

  .earnings-transparency {
    padding: 30px 20px;
  }

  .contact-title {
    font-size: 2rem;
  }

  .contact-btn {
    width: 220px;
    height: 50px;
    font-size: 0.95rem;
  }
}
</style>
